<template>
  <b-card>
    <div
      v-if="isEmptyState"
      class="text-center text-muted py-5"
    >
      <h5>Нет данных для отображения</h5>
      <p class="mb-2">
        Для начала работы нажмите кнопку <strong>«Старт»</strong> или <strong>«Обновить»</strong> в панели мониторинга
      </p>
      <small class="text-muted">
        💡 Данные будут загружены и обновлены автоматически
      </small>
    </div>

    <div v-else>
      <div class="results-header mb-3">
        <div class="results-summary">
          <template v-if="serverPagination">
            <strong>Всего участников:</strong> {{ serverTotalCount }}
            <span class="ml-3"><strong>На странице:</strong> {{ paginatedResults.length }}</span>
          </template>
          <template v-else>
            <strong>Всего участников:</strong> {{ results.length }}
            <span class="ml-3"><strong>Отображается:</strong> {{ filteredResults.length }}</span>
          </template>
          <span class="ml-3"><strong>Нарушения:</strong> {{ violationCount }}</span>
        </div>
        <div class="results-actions">
          <b-button
            v-b-tooltip.hover.left="'Экспортировать текущие результаты в CSV файл. Учитываются активные фильтры.'"
            variant="outline-primary"
            size="sm"
            :disabled="isExporting"
            @click="$emit('export')"
          >
            <b-spinner
              v-if="isExporting"
              small
              class="mr-1"
            />
            <template v-else>
              📥
            </template>
            Экспорт CSV
          </b-button>
        </div>
      </div>

      <b-alert
        variant="info"
        show
        class="mb-3 hints-alert"
      >
        <div class="d-flex align-items-start">
          <div class="hint-icon">
            💡
          </div>
          <div class="hint-content">
            <div class="hint-text">
              <strong>Подсказка:</strong>
              Нажмите <b-icon
                icon="eye"
                font-scale="0.9"
                class="mx-1"
              /> <strong>для просмотра всех считываний</strong> участника.
              Кликните на <strong>результат (от старта гонки)</strong> для установки статуса (НФ/ДСК/НС).
              Кликните на <strong>ячейку чекпоинта</strong> для редактирования времени.
            </div>
            <div
              v-if="showDualResultColumns"
              class="hint-text mt-2"
            >
              <strong>Два времени в таблице:</strong>
              <strong>От старта гонки</strong> — gun time от общего старта;
              <strong>Первая → последняя точка</strong> — chip-сегмент по списку чекпоинтов дистанции (как в настройке «Chip Time»).
            </div>
            <div
              v-if="showChipRankingForFilteredGroup"
              class="hint-text mt-2"
            >
              <strong>Места при фильтре группы:</strong>
              для этой дистанции включён официальный Chip Time — при выбранной одной группе колонка
              <strong>«№»</strong>
              и порядок строк соответствуют
              <strong>чип-тайму</strong>.
            </div>
            <div class="legend-section">
              <div class="legend-title">
                Индикаторы:
              </div>
              <div class="legend-items">
                <div class="legend-item">
                  <span class="indicator override">✎</span>
                  <span class="legend-label">Ручное переопределение (снять — в редакторе чекпоинта)</span>
                </div>
                <div class="legend-item">
                  <b-badge
                    variant="info"
                    pill
                  >
                    2
                  </b-badge>
                  <span class="legend-label">Альтернативных считываний</span>
                </div>
                <div class="legend-item">
                  <span class="indicator violation" />
                  <span class="legend-label">Нарушение: пропуски точек, нет старта, ранний старт или ограничения</span>
                </div>
                <div class="legend-item">
                  <span class="leader-badge">🥇</span>
                  <span class="legend-label">Лидер в категории</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-alert>

      <div
        v-if="!paginatedResults.length"
        class="text-center text-muted py-4 border rounded mb-2"
      >
        <h6 class="mb-1">
          По текущим фильтрам данных нет
        </h6>
        <small v-if="filters.violationOnly">Отключите фильтр «Только нарушения» или измените другие фильтры.</small>
      </div>

      <div
        v-else
        class="table-responsive"
      >
        <b-table
          :items="paginatedResults"
          :fields="tableFields"
          small
          striped
          hover
          bordered
          sticky-header="600px"
          :tbody-tr-class="getRowClass"
          @head-clicked="onHeadClicked"
        >
          <!-- Place column -->
          <template #cell(place)="{ index }">
            <strong>{{ getPlace(index) }}</strong>
          </template>

          <!-- Participant column -->
          <template #cell(participant)="{ item }">
            <div class="participant-cell">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <div class="participant-name">
                    <strong>{{ getFullName(item) }}</strong>
                  </div>
                  <div class="participant-meta">
                    <small class="text-muted">#{{ item.num || item.userId }}</small>
                    <b-badge
                      v-if="hasEarlyChipStartViolation(item)"
                      variant="warning"
                      class="ml-1"
                    >
                      Ранний старт по чипу
                    </b-badge>
                  </div>
                </div>
                <b-button
                  v-b-tooltip.hover.left="'Просмотреть все считывания участника'"
                  size="sm"
                  variant="outline-primary"
                  class="btn-icon"
                  @click="$emit('view-details', item)"
                >
                  <b-icon
                    icon="eye"
                    font-scale="1"
                  />
                </b-button>
              </div>
            </div>
          </template>

          <!-- Single result column (gun vs chip via filters) -->
          <template #cell(result)="{ item }">
            <div
              class="result-cell"
              :class="getResultClass(item)"
              @click="$emit('open-status', item)"
            >
              <span
                v-if="item.status === 'dnf'"
                class="badge badge-warning"
              >НФ</span>
              <span
                v-else-if="item.status === 'dsq'"
                class="badge badge-danger"
              >ДСК</span>
              <span
                v-else-if="item.status === 'dns'"
                class="badge badge-secondary"
              >НС</span>
              <span
                v-else-if="!item.hasFinish"
                class="text-muted"
              >—</span>
              <span v-else>
                <strong>{{ getDisplayResult(item) }}</strong>
                <small
                  v-if="filters.resultType === 'chip' && showGunFallbackInfo(item)"
                  class="text-warning d-block"
                >
                  показан gun time
                </small>
                <small
                  v-else-if="filters.resultType === 'chip' && !item.chipTime"
                  class="text-muted d-block"
                >
                  нет START
                </small>
              </span>
            </div>
          </template>

          <!-- Dual columns: officialTimeMode chipTime + ≥2 checkpoints -->
          <template #cell(resultGun)="{ item }">
            <div
              class="result-cell"
              :class="getResultClass(item)"
              @click="$emit('open-status', item)"
            >
              <span
                v-if="item.status === 'dnf'"
                class="badge badge-warning"
              >НФ</span>
              <span
                v-else-if="item.status === 'dsq'"
                class="badge badge-danger"
              >ДСК</span>
              <span
                v-else-if="item.status === 'dns'"
                class="badge badge-secondary"
              >НС</span>
              <span
                v-else-if="!item.hasFinish"
                class="text-muted"
              >—</span>
              <span v-else>
                <strong>{{ item.gunTime || item.result || '—' }}</strong>
              </span>
            </div>
          </template>

          <template #cell(resultChip)="{ item }">
            <div
              v-b-tooltip.hover.top="'Интервал от первой до последней точки в списке трека (режим Chip Time в конфиге дистанции)'"
              class="result-cell result-cell--chip-segment"
            >
              <span
                v-if="item.status === 'dnf' || item.status === 'dsq' || item.status === 'dns'"
                class="text-muted"
              >—</span>
              <span
                v-else-if="!item.hasFinish"
                class="text-muted"
              >—</span>
              <span v-else-if="item.status === 'finished' && item.chipTime">
                <strong>{{ item.chipTime }}</strong>
              </span>
              <span
                v-else
                class="text-muted"
              >
                —
                <small
                  v-if="showGunFallbackInfo(item)"
                  class="d-block text-warning"
                >использован gun time</small>
                <small
                  v-else-if="item.status === 'finished' && item.hasFinish"
                  class="d-block"
                >нет интервала</small>
              </span>
            </div>
          </template>

          <!-- Gap column -->
          <template #cell(gap)="{ item }">
            <!-- Has gap in distance - show it -->
            <span
              v-if="item.gapToLeaderInDistance"
              v-b-tooltip.hover="'Отставание от лидера в рамках дистанции и группы участника'"
              class="text-muted gap-value"
            >
              +{{ item.gapToLeaderInDistance }}
            </span>
            <!-- Is leader in category - show indicator -->
            <span
              v-else-if="item.hasFinish && item.status === 'finished' && item.result"
              v-b-tooltip.hover="'Лидер в категории (дистанция + группа)'"
              class="leader-indicator"
            >
              🥇
            </span>
            <!-- Fallback to general gap -->
            <span
              v-else-if="item.gapToLeader"
              class="text-muted gap-value"
            >
              +{{ item.gapToLeader }}
            </span>
          </template>

          <!-- Checkpoint columns -->
          <template
            v-for="cp in checkpointFields"
            v-slot:[`cell(cp_${cp.key})`]="{ item }"
          >
            <div
              :key="cp.key"
              class="checkpoint-cell"
              :class="getCheckpointClass(item, cp)"
              @click="openCheckpointEditor(item, cp)"
            >
              <div class="cp-time">
                {{ getCheckpointTime(item, cp) }}
              </div>
              <div
                v-if="isCheckpointOverridden(item, cp)"
                class="cp-indicator"
              >
                ✎
              </div>
              <div
                v-if="hasAlternatives(item, cp)"
                class="cp-alternatives"
              >
                <b-badge
                  variant="info"
                  pill
                >
                  {{ getAlternativesCount(item, cp) }}
                </b-badge>
              </div>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Pagination: server-backed -->
      <div
        v-if="serverPagination && serverTotalCount > 0"
        class="d-flex justify-content-between align-items-center mt-3"
      >
        <div>
          <b-form-select
            :value="serverPerPage"
            :options="[20, 50, 100, 200]"
            size="sm"
            style="width: 100px"
            @input="onServerPerPageChange"
          />
        </div>

        <b-pagination
          :value="serverPage"
          :total-rows="serverTotalCount"
          :per-page="serverPerPage"
          size="sm"
          @input="onServerPageInput"
        />

        <div class="text-muted small">
          Показано {{ serverRangeStart }} - {{ serverRangeEnd }}
          из {{ serverTotalCount }}
        </div>
      </div>

      <!-- Pagination: client (legacy) -->
      <div
        v-else-if="!serverPagination && totalPages > 1"
        class="d-flex justify-content-between align-items-center mt-3"
      >
        <div>
          <b-form-select
            v-model="perPage"
            :options="[20, 50, 100, 200]"
            size="sm"
            style="width: 100px"
          />
        </div>

        <b-pagination
          v-model="currentPage"
          :total-rows="filteredResults.length"
          :per-page="perPage"
          size="sm"
        />

        <div class="text-muted small">
          Показано {{ (currentPage - 1) * perPage + 1 }} -
          {{ Math.min(currentPage * perPage, filteredResults.length) }}
          из {{ filteredResults.length }}
        </div>
      </div>
    </div>
  </b-card>
</template>

<script>
import {
  BCard, BTable, BFormSelect, BPagination, BBadge, BButton, BAlert, BIcon, BSpinner, VBTooltip,
} from 'bootstrap-vue'
import { getFullName } from '../../utils/formatters'

export default {
  name: 'ResultsGrid',
  components: {
    BCard,
    BTable,
    BFormSelect,
    BPagination,
    BBadge,
    BButton,
    BAlert,
    BIcon,
    BSpinner,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    results: {
      type: Array,
      default: () => [],
    },
    checkpoints: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    isExporting: {
      type: Boolean,
      default: false,
    },
    serverPagination: {
      type: Boolean,
      default: false,
    },
    serverTotalCount: {
      type: Number,
      default: 0,
    },
    serverViolationCount: {
      type: Number,
      default: 0,
    },
    serverTotalPages: {
      type: Number,
      default: 0,
    },
    serverPage: {
      type: Number,
      default: 1,
    },
    serverPerPage: {
      type: Number,
      default: 50,
    },
    /** { userId?, num?, point, lap } while checkpoint editor is open; null otherwise */
    editingCheckpointCell: {
      type: Object,
      default: null,
    },
    /** From distance config — together with ≥2 checkpoints enables two result columns */
    officialTimeMode: {
      type: String,
      default: 'gunTime',
    },
  },
  data() {
    return {
      currentPage: 1,
      perPage: 50,
      sortByCheckpoint: null,
      sortDirection: 'asc',
    }
  },
  computed: {
    isEmptyState() {
      if (this.serverPagination) {
        return (this.serverTotalCount || 0) === 0
      }
      return !this.results || this.results.length === 0
    },

    serverRangeStart() {
      if (!this.serverTotalCount) return 0
      return (this.serverPage - 1) * this.serverPerPage + 1
    },

    serverRangeEnd() {
      return Math.min(this.serverPage * this.serverPerPage, this.serverTotalCount)
    },

    /** Checkpoints shown as table columns (last point omitted in cumulative mode — duplicates total time). */
    visibleCheckpoints() {
      const list = Array.isArray(this.checkpoints) ? this.checkpoints : []
      const cumulative = this.filters && this.filters.timeDisplayMode === 'cumulative'
      if (cumulative && list.length >= 2) {
        return list.slice(0, -1)
      }
      return list
    },

    checkpointFields() {
      return this.visibleCheckpoints.map(cp => {
        const base = cp.header || cp.point
        const label = cp.segmentName ? `${cp.segmentName}: ${base}` : base
        return {
          key: `${cp.point}_${cp.lap || 0}`,
          label,
          point: cp.point,
          lap: cp.lap,
          segmentNumber: cp.segmentNumber,
          segmentName: cp.segmentName,
        }
      })
    },

    showDualResultColumns() {
      const n = Array.isArray(this.checkpoints) ? this.checkpoints.length : 0
      return this.filters.chipTimeEnabled !== false && this.officialTimeMode === 'chipTime' && n >= 2
    },

    showChipRankingForFilteredGroup() {
      const g = this.filters && String(this.filters.group || '').trim()
      return !!g && this.officialTimeMode === 'chipTime'
    },

    violationCount() {
      if (this.serverPagination) {
        if (this.filters && this.filters.violationOnly) {
          return this.serverTotalCount || 0
        }
        return this.serverViolationCount || 0
      }
      return (this.filteredResults || []).filter(item => this.isViolationRow(item)).length
    },

    tableFields() {
      const fields = [
        { key: 'place', label: '№' },
        { key: 'participant', label: 'Участник' },
        { key: 'team', label: 'Команда' },
        { key: 'group', label: 'Группа' },
        { key: 'distance', label: 'Дистанция' },
      ]

      if (this.showDualResultColumns) {
        fields.push(
          {
            key: 'resultGun',
            label: 'От старта гонки',
            thClass: 'result-col-gun',
            thAttr: {
              title: 'Gun time — от общего времени старта гонки (конфиг / участник / глобальный старт)',
            },
          },
          {
            key: 'resultChip',
            label: 'Первая → последняя точка',
            thClass: 'result-col-chip',
            thAttr: {
              title: 'Chip time — от первой до последней точки в списке трека дистанции',
            },
          },
        )
      } else {
        fields.push({ key: 'result', label: 'Результат' })
      }

      fields.push({ key: 'gap', label: 'Отставание' })

      // Add checkpoint fields
      this.checkpointFields.forEach(cp => {
        const key = `cp_${cp.key}`
        const isActive = this.sortByCheckpoint === key
        fields.push({
          key,
          label: cp.label,
          sortable: true,
          thClass: isActive ? 'checkpoint-header-sorted' : 'checkpoint-header-sortable',
          thAttr: { 'data-sort-key': key },
        })
      })

      return fields
    },

    filteredResults() {
      const filters = this.filters || {}

      if (this.serverPagination) {
        let filtered = this.results || []
        if (filters.status) {
          filtered = filtered.filter(item => item.status === filters.status)
        }
        return filtered
      }

      let filtered = this.results || []

      // Search filter
      if (filters.search) {
        const search = filters.search.toLowerCase()
        filtered = filtered.filter(item => {
          const name = (item.name || '').toLowerCase()
          const secondName = (item.secondName || '').toLowerCase()
          const num = (item.num || item.userId || '').toString().toLowerCase()

          return name.includes(search)
                 || secondName.includes(search)
                 || num.includes(search)
        })
      }

      // Gender filter
      if (filters.gender) {
        filtered = filtered.filter(item => item.gender === filters.gender)
      }

      // Group filter
      if (filters.group) {
        filtered = filtered.filter(item => item.group === filters.group)
      }

      // Status filter
      if (filters.status) {
        filtered = filtered.filter(item => item.status === filters.status)
      }

      if (filters.violationOnly) {
        filtered = filtered.filter(item => this.isViolationRow(item))
      }
      if (filters.earlyChipStartOnly) {
        filtered = filtered.filter(item => this.hasEarlyChipStartViolation(item))
      }

      return filtered
    },

    sortedResults() {
      if (!this.sortByCheckpoint || !this.sortByCheckpoint.startsWith('cp_')) {
        return this.filteredResults
      }
      const cpKey = this.sortByCheckpoint.replace(/^cp_/, '')
      const cp = this.checkpointFields.find(f => (`${f.point}_${f.lap || 0}`) === cpKey)
      if (!cp) return this.filteredResults

      const dir = this.sortDirection === 'desc' ? -1 : 1
      const useSplit = this.filters?.timeDisplayMode === 'split'

      return [...this.filteredResults].sort((a, b) => {
        const aMs = this.parseCheckpointTimeMs(a, cp, useSplit)
        const bMs = this.parseCheckpointTimeMs(b, cp, useSplit)
        if (aMs === bMs) return 0
        if (aMs == null) return 1
        if (bMs == null) return -1
        return (aMs - bMs) * dir
      })
    },

    paginatedResults() {
      if (this.serverPagination) {
        return this.sortedResults
      }
      const start = (this.currentPage - 1) * this.perPage
      const end = start + this.perPage
      return this.sortedResults.slice(start, end)
    },

    totalPages() {
      if (this.serverPagination) {
        return Math.max(1, this.serverTotalPages || 1)
      }
      return Math.ceil(this.sortedResults.length / this.perPage)
    },
  },
  watch: {
    filteredResults() {
      if (!this.serverPagination) {
        this.currentPage = 1
      }
    },
    sortByCheckpoint() {
      this.currentPage = 1
    },
    'filters.timeDisplayMode': 'clearInvalidCheckpointSort',
    checkpoints: 'clearInvalidCheckpointSort',
  },
  methods: {
    clearInvalidCheckpointSort() {
      if (!this.sortByCheckpoint || !this.sortByCheckpoint.startsWith('cp_')) return
      const cpKey = this.sortByCheckpoint.replace(/^cp_/, '')
      if (!this.checkpointFields.some(f => f.key === cpKey)) {
        this.sortByCheckpoint = null
      }
    },

    getRowClass(item) {
      if (this.isViolationRow(item)) {
        return 'anomaly-row'
      }
      return ''
    },

    isViolationRow(item) {
      if (!item) return false
      const status = String(item.status || '').toLowerCase()
      if (status === 'dnf' || status === 'dns' || status === 'dsq') return true
      if (status === 'finished' && !item.hasFinish) return true
      if (item.hasMissingIntermediateCheckpoints) return true
      if (item.hasConstraintViolation) return true
      if (this.hasConstraintViolation(item)) return true
      const types = Array.isArray(item.violationType) ? item.violationType : []
      return types.length > 0
    },

    getFullName(item) {
      return getFullName(item)
    },

    getPlace(index) {
      if (this.serverPagination) {
        return (this.serverPage - 1) * this.serverPerPage + index + 1
      }
      return (this.currentPage - 1) * this.perPage + index + 1
    },

    onServerPageInput(page) {
      this.$emit('page-change', page)
    },

    onServerPerPageChange(val) {
      this.$emit('per-page-change', val)
    },

    getResultClass(item) {
      return {
        clickable: true,
        'has-status': item.status && item.status !== 'finished',
        'no-finish': !item.hasFinish,
      }
    },

    getDisplayResult(item) {
      // TASK-014: Show Chip Time if selected and available, otherwise Gun Time
      if (this.filters.chipTimeEnabled !== false && this.filters.resultType === 'chip' && item.chipTime) {
        return item.chipTime
      }
      return item.result
    },

    showGunFallbackInfo(item) {
      if (!item) return false
      if (this.officialTimeMode !== 'chipTime') return false
      if (item.status !== 'finished' || !item.hasFinish) return false
      if (item.timeSource === 'gun') return true
      return !!item.isGunFallback
    },

    hasConstraintViolation(item) {
      if (!item) return false
      if (item.hasConstraintViolation) return true
      const checkpointResults = item.checkpointResults || []
      return checkpointResults.some(cp => cp && cp.violatesTimeConstraint)
    },
    hasEarlyChipStartViolation(item) {
      const types = Array.isArray(item && item.violationType) ? item.violationType : []
      return types.includes('too_early_start')
    },

    getCheckpointTime(item, cp) {
      const checkpoint = this.findCheckpoint(item, cp)
      if (!checkpoint) return '—'

      if (this.filters.timeDisplayMode === 'cumulative') {
        return checkpoint.time || '—'
      }
      return checkpoint.displayTime || checkpoint.splitTime || checkpoint.time || '—'
    },

    getCheckpointClass(item, cp) {
      const checkpoint = this.findCheckpoint(item, cp)

      return {
        clickable: true,
        'is-overridden': checkpoint && checkpoint.isOverridden,
        'has-alternatives': checkpoint && checkpoint.additionalOptions && checkpoint.additionalOptions.length > 0,
        'violates-constraint': checkpoint && checkpoint.violatesTimeConstraint,
        'cell-editing-active': this.isEditingCheckpointCell(item, cp),
      }
    },

    isEditingCheckpointCell(item, cp) {
      const ctx = this.editingCheckpointCell
      if (!ctx || !item) return false
      if (cp.point !== ctx.point || (cp.lap || 0) !== (ctx.lap || 0)) return false

      const rowUid = item.userId
      const ctxUid = ctx.userId
      if (
        ctxUid !== undefined && ctxUid !== null && ctxUid !== ''
        && rowUid !== undefined && rowUid !== null && rowUid !== ''
      ) {
        return String(rowUid) === String(ctxUid)
      }

      const rowNum = item.num
      const ctxNum = ctx.num
      if (
        ctxNum !== undefined && ctxNum !== null && ctxNum !== ''
        && rowNum !== undefined && rowNum !== null && rowNum !== ''
      ) {
        return String(rowNum) === String(ctxNum)
      }

      return false
    },

    isCheckpointOverridden(item, cp) {
      const checkpoint = this.findCheckpoint(item, cp)
      return checkpoint && checkpoint.isOverridden
    },

    hasAlternatives(item, cp) {
      const checkpoint = this.findCheckpoint(item, cp)
      return checkpoint && checkpoint.additionalOptions && checkpoint.additionalOptions.length > 0
    },

    getAlternativesCount(item, cp) {
      const checkpoint = this.findCheckpoint(item, cp)
      return checkpoint && checkpoint.additionalOptions ? checkpoint.additionalOptions.length : 0
    },

    findCheckpoint(item, cp) {
      if (!item.checkpointResults) return null

      return item.checkpointResults.find(
        c => c.point === cp.point && (c.lap || 0) === (cp.lap || 0),
      )
    },

    parseCheckpointTimeMs(item, cp, useSplit) {
      const checkpoint = this.findCheckpoint(item, cp)
      if (!checkpoint) return null
      const raw = useSplit ? (checkpoint.displayTime || checkpoint.splitTime || checkpoint.time) : checkpoint.time
      if (!raw || String(raw).trim() === '' || raw === '—') return null
      const str = String(raw).trim()
      const parts = str.split(':')
      if (parts.length === 3) {
        const [h, m, sPart] = parts
        const [s, ms = '0'] = sPart.split('.')
        return (parseInt(h, 10) || 0) * 3600000 + (parseInt(m, 10) || 0) * 60000 + (parseInt(s, 10) || 0) * 1000 + parseInt(ms.slice(0, 3).padEnd(3, '0'), 10)
      }
      if (parts.length === 2) {
        const [m, sPart] = parts
        const [s, ms = '0'] = (sPart || '0').split('.')
        return (parseInt(m, 10) || 0) * 60000 + (parseInt(s, 10) || 0) * 1000 + parseInt(ms.slice(0, 3).padEnd(3, '0'), 10)
      }
      return null
    },

    onHeadClicked(key) {
      if (!key || !key.startsWith('cp_')) return
      if (this.sortByCheckpoint === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortByCheckpoint = key
        this.sortDirection = 'asc'
      }
    },

    openCheckpointEditor(item, cp) {
      const checkpoint = this.findCheckpoint(item, cp)

      this.$emit('edit-checkpoint', {
        participant: item,
        checkpoint: checkpoint || { point: cp.point, lap: cp.lap, header: cp.label },
      })
    },

    emitUpdate() {
      this.$emit('update', { ...this.localFilters })
    },

    resetFilters() {
      this.localFilters = {
        search: '',
        gender: '',
        distance: '',
        group: '',
        status: '',
      }
      this.emitUpdate()
    },
  },
}
</script>

<style scoped>
.filters-card {
  margin-bottom: 1rem;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.857rem 1rem;
  background: #f8f8f8;
  border-left: 3px solid #7367f0;
  border-radius: 0.358rem;
  margin-bottom: 1rem;
}

.results-summary {
  font-size: 0.95rem;
  color: #5e5873;
}

.results-actions {
  display: flex;
  gap: 0.571rem;
}

.participant-cell {
  min-width: 150px;
}

.participant-name {
  margin-bottom: 0.25rem;
  font-weight: 500;
  color: #5e5873;
}

.participant-meta {
  font-size: 0.857rem;
  color: #b9b9c3;
}

.result-cell--chip-segment {
  cursor: help;
  padding: 0.5rem;
}

.result-cell {
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.358rem;
  transition: all 0.2s;
  font-weight: 500;
}

.result-cell.clickable:hover {
  background: #f3f2f7;
}

.result-cell.has-status {
  font-weight: 600;
}

.checkpoint-cell {
  cursor: pointer;
  padding: 0.5rem;
  position: relative;
  min-height: 40px;
  transition: all 0.2s;
  border-radius: 0.358rem;
}

.checkpoint-cell.clickable:hover {
  background: #f3f2f7;
}

.checkpoint-cell.is-overridden {
  background: #fff6ed;
  border-left: 3px solid #ff9f43;
}

.checkpoint-cell.violates-constraint {
  background: #fff5f5;
  border-left: 3px solid #ea5455;
}

.checkpoint-cell.cell-editing-active {
  background: #f0edfc;
  box-shadow: 0 0 0 2px #7367f0;
  position: relative;
  z-index: 1;
}

.checkpoint-cell.cell-editing-active.is-overridden {
  background: #f5f0ff;
  border-left-color: #7367f0;
}

.cp-time {
  font-weight: 500;
  color: #5e5873;
}

.cp-alternatives {
  margin-top: 0.25rem;
}

.cp-indicator {
  position: absolute;
  top: 4px;
  right: 4px;
  font-size: 0.857rem;
  color: #ff9f43;
}

/* Hints alert styling */
.hints-alert {
  padding: 1.143rem !important;
  border-radius: 0.428rem;
}

.hint-icon {
  font-size: 1.5rem;
  line-height: 1;
  margin-right: 1rem;
  flex-shrink: 0;
}

.hint-content {
  flex: 1;
}

.hint-text {
  margin-bottom: 1rem;
  line-height: 1.6;
  color: #5e5873;
}

.hint-text strong {
  color: #5e5873;
}

.legend-section {
  padding-top: 0.857rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.legend-title {
  font-weight: 600;
  margin-bottom: 0.714rem;
  color: #5e5873;
  font-size: 0.857rem;
}

.legend-items {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.714rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.571rem;
  padding: 0.428rem 0.714rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 0.358rem;
  transition: all 0.2s;
}

.legend-item:hover {
  background: rgba(255, 255, 255, 0.8);
  transform: translateX(2px);
}

.legend-label {
  font-size: 0.857rem;
  color: #6e6b7b;
  font-weight: 400;
}

.indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 0.358rem;
  font-size: 0.857rem;
  flex-shrink: 0;
}

.indicator.override {
  background: #fff6ed;
  border: 1px solid #ff9f43;
  color: #ff9f43;
  font-weight: 600;
}

.indicator.violation {
  background: #fff5f5;
  border: 1px solid #ea5455;
}

.leader-badge {
  font-size: 1.2rem;
  line-height: 1;
  flex-shrink: 0;
}

.gap-value {
  font-weight: 500;
  cursor: help;
}

.leader-indicator {
  font-size: 1.2rem;
  cursor: help;
  filter: drop-shadow(0 1px 2px rgba(255, 193, 7, 0.3));
}

.btn-icon {
  padding: 0.286rem 0.428rem;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.btn-icon:hover {
  opacity: 1;
}

.participant-cell .d-flex {
  gap: 0.571rem;
}

.anomaly-row {
  background-color: #fff8e1 !important;
}

.checkpoint-header-sortable {
  cursor: pointer;
}

.checkpoint-header-sorted {
  cursor: pointer;
  background-color: #e8e7ed;
}
</style>
